/**
 * Per-visa document checklists.
 *
 * Source of truth: ADI's existing GHL snippet library, captured in
 * `tasks/stage2-forms/snippets/snippet-library.md`, plus Jane (Nhu)'s UAT
 * document-checklist matrix (`docs/uat-jade/jane-feedback-gdoc.txt`, the
 * "DOCUMENT CHECKLIST – Partner Visa" table).
 *
 * Per Eli's 23 Apr feedback (1.9): the client should see the FULL list of
 * documents we'll eventually need, with irrelevant items greyed out by stage.
 * Required vs Recommended is a UI signal, not a hard gate, so a client can
 * still upload anything they have early.
 *
 * CONDITIONAL ENGINE (Jane's matrix)
 * ----------------------------------
 * Documents now appear / are-required based on the matter's INTAKE ANSWERS, not
 * just visa subclass + variant. The engine takes an `IntakeAnswers` object and
 * computes, per document, whether it is `required`, `optional`, or hidden
 * entirely (omitted from the returned list). Each document also carries an
 * `owner` (`applicant` / `sponsor` / `staff`) so the Documents view can decide
 * who gets an upload control.
 *
 * Process-tracking items from the snippets ("Onboarding Email", "Draft Created",
 * "Final Invoice paid", etc.) are intentionally excluded here. Those are staff
 * pipeline-stage flags, not client-facing document uploads.
 */

import { hasSponsorForm } from './schema-runtime';

export type DocCategory =
  | 'identity'
  | 'relationship'
  | 'character'
  | 'health'
  | 'evidence'
  | 'sponsor'
  | 'inviter'
  | 'forms'
  | 'travel'
  | 'financial'
  | 'household'
  | 'social';

/**
 * Who is responsible for uploading an item.
 *  - 'applicant' (default), the visa applicant uploads.
 *  - 'sponsor', the Australian sponsor uploads via their own sponsor-mode
 *    Documents view. In the applicant view these appear as greyed,
 *    non-actionable rows tagged "Sponsor uploads this".
 *  - 'staff', ADI staff upload this (e.g. Form 80 / Form 956, prepared and
 *    sent for signature after lodgement). These are STAFF-UPLOAD-ONLY: no
 *    upload control is shown to the applicant or the sponsor.
 */
export type DocOwner = 'applicant' | 'sponsor' | 'staff';

export interface DocItem {
  key: string; // stable identifier used as `documents.checklist_item`
  label: string;
  category: DocCategory;
  required: boolean;
  /** Optional helper text shown beneath the row. */
  hint?: string;
  /**
   * Optional condition. If provided, the doc only applies when the matter's
   * variant matches one of these (e.g. ['married'] hides the doc on de-facto
   * matters). Empty/undefined = always relevant.
   */
  appliesToVariants?: string[];
  /** Who is responsible for uploading this item. Defaults to 'applicant'. */
  owner?: DocOwner;
  /**
   * Staff-upload-only. When true the document is visible/uploadable ONLY to
   * staff, the applicant and sponsor never see an upload control for it.
   * Implied by `owner: 'staff'`; kept as an explicit flag so the Documents
   * view can gate the control without re-deriving from owner.
   */
  staffOnly?: boolean;
  /**
   * Two-stage split (partner visas only). 1 = pre-lodgement (needed to lodge:
   * identity, relationship certificate, police/health, core forms). 2 =
   * post-lodgement evidence phase (the four relationship-evidence pillars,
   * Form 888 witness declarations, Form-80-depth). Set only on the partner
   * checklist (820/309/300); undefined elsewhere = always shown. The Documents
   * view greys stage-2 rows and excludes them from the counter until the
   * pre-lodgement intake latches. See the walkthrough plan §1.6.
   */
  stage?: 1 | 2;
}

/**
 * Normalised intake answers consumed by the conditional engine.
 *
 * This is a clean, decoupled contract, NOT the raw `intake_field_data` storage
 * shape. Documents.tsx maps the stored `<section>.<field>` rows into this object
 * defensively (see `intakeAnswersFromFieldData` in Documents.tsx). Every field
 * is optional; unknown answers default conservatively (a conditional doc stays
 * hidden until its trigger is confirmed, rather than being wrongly required).
 *
 * Field provenance (v2/v3 canonical names; see Documents.tsx intakeAnswersFromFieldData):
 *   relationshipStatus            <- applicant.relationship.status
 *   livedSeparately               <- applicant.relationship.lived_separately
 *   applicantEverEnteredAustralia <- applicant.visa_history.previously_applied
 *   dependentChildWithSponsor     <- applicant.relationship.number_of_children / migrating_family.is_child_of_relationship
 *   applicantHadPriorRelationship <- applicant.previous_relationships.has_previous
 *   applicantPriorRelationshipType<- applicant.previous_relationships.entries[].relationship_type
 *   sponsorHadPriorRelationship   <- sponsor.previous_relationships.has_previous
 *   jointBankAccount              <- applicant.evidence.financial.joint_bank_account
 *   jointAssets                   <- applicant.evidence.financial.joint_assets
 *   regularMoneyTransfers         <- applicant.evidence.financial.regular_money_transfers
 *   jointRentalOrOwnership        <- applicant.evidence.household.joint_lease_or_ownership
 *   livingTogether                <- applicant.evidence.household.living_together
 *   jointTravel                   <- applicant.evidence.social.joint_travel
 *   jointInvitations              <- applicant.evidence.social.joint_invitations
 *   socialMediaVisible            <- applicant.evidence.social.social_media
 *   willsOrBeneficiaries          <- applicant.evidence.commitment.will_beneficiary / super_insurance_beneficiary
 *
 * The applicant.evidence.* signals come from the four "Relationship evidence"
 * tabs added in Jane's reorg (2026-07-16). On matters where those tabs are
 * untouched the signals are undefined and the related docs show as optional.
 */
export interface IntakeAnswers {
  /** 'married' | 'defacto' | 'engaged' (normalised lowercase, no spaces). */
  relationshipStatus?: 'married' | 'defacto' | 'engaged';
  /**
   * Australian state/territory code where a de facto relationship is (or would
   * be) registered, e.g. 'NSW'. WA and NT do not offer relationship
   * registration, so the state-registered certificate documents are skipped for
   * them (R7, Jane 2026-07-21).
   */
  registeredState?: string;
  jointBankAccount?: boolean;
  jointAssets?: boolean;
  regularMoneyTransfers?: boolean;
  /** Joint ownership OR joint rental agreement for the shared address. */
  jointRentalOrOwnership?: boolean;
  dependentChildWithSponsor?: boolean;
  /** Lived separately and apart for any period since committing. */
  livedSeparately?: boolean;
  /**
   * Whether the applicant has ever entered Australia. 309/300 applicants who
   * have never entered AU don't need the AU police check (Jane F).
   */
  applicantEverEnteredAustralia?: boolean;
  applicantHadPriorRelationship?: boolean;
  /** 'married' | 'defacto' | 'separated' | 'never-married', drives prior-relationship docs. */
  applicantPriorRelationshipType?: 'married' | 'defacto' | 'separated' | 'never-married';
  applicantPriorPartnerDeceased?: boolean;
  sponsorHadPriorRelationship?: boolean;
  sponsorPriorRelationshipType?: 'married' | 'defacto' | 'separated' | 'never-married';
  sponsorPriorPartnerDeceased?: boolean;
  /**
   * 600 tourist stream only. True when the applicant has declared an inviter
   * or supporter (an Australia-based person providing accommodation, funds, or
   * an invitation to visit). Drives the inviter document set. Sourced from the
   * 1419 field `application.has_supporters`.
   *
   * Since Wave 5 an inviter MAY also be a party on the matter with their own
   * login and short form (staff add them at matter creation). That is a second,
   * independent trigger for the same document set: `hasInviterParty` on
   * ChecklistOptions. The documents stay applicant-owned either way: the
   * applicant collects and uploads them.
   */
  hasInviter?: boolean;
  /** Couple currently live together (820 Household evidence tab). */
  livingTogether?: boolean;
  /** Couple have travelled together (Social evidence tab). */
  jointTravel?: boolean;
  /** Couple are invited to events together (Social evidence tab). */
  jointInvitations?: boolean;
  /** Relationship is visible on social media (Social evidence tab). */
  socialMediaVisible?: boolean;
  /** Either partner named the other in a will or as a super/insurance beneficiary. */
  willsOrBeneficiaries?: boolean;
  /**
   * 600 tourist: applicant selected "Self employed" as their employment status
   * (1419 item 28). Flips the "Proof of business registration" document from
   * optional to required. Sourced from `applicant.employment_status`.
   */
  applicantSelfEmployed?: boolean;
}

const UNIVERSAL_APPLICANT_DOCS: DocItem[] = [
  { key: 'applicant_passport', label: 'Applicant passport (bio page)', category: 'identity', required: true, hint: 'Upload as early as you can. We need this to confirm visa expiry and current status.' },
  { key: 'applicant_birth_certificate', label: 'Birth certificate', category: 'identity', required: false },
  { key: 'applicant_national_id', label: 'National ID card', category: 'identity', required: false, hint: 'Required by Home Affairs for applicants from some countries.' },
  { key: 'applicant_driver_licence', label: 'Driver licence', category: 'identity', required: false },
  { key: 'applicant_other_ids', label: 'Other identity documents', category: 'identity', required: false, hint: 'e.g. household registration, family book, photo card.' },
  { key: 'health_check_referral', label: 'Health check referral letter (eMedical)', category: 'health', required: true, owner: 'staff', staffOnly: true, hint: 'We generate this for you with your HAP ID. Your results go straight to the department via eMedical, nothing to upload here.' },
];

// ---------------------------------------------------------------------------
// Sponsor-side checklist (canonical sponsor_* keys)
// ---------------------------------------------------------------------------
//
// Sponsors upload their own forms / IDs / clearances. The applicant cannot
// upload these on the sponsor's behalf, the sponsor signs in to their own
// portal account (sponsor-mode) and uploads via the same Documents page,
// scoped to their sponsor checklist + a separate storage path prefix.
//
// On the applicant view, these items still surface (greyed/tagged) so the
// applicant can see the full picture of what's outstanding across the matter,
// but they're not actionable from the applicant's side.
//
// Visa subclasses with a sponsor obligation: 309/820/100/801 (partner), 300
// (PMV). 461 has its own NZ-citizen sponsor surface (handled separately
// below); visitor visas (600-family) use 600-family-specific sponsor rows.

const SPONSOR_OWNED_DOCS: DocItem[] = [
  {
    key: 'sponsor_passport_bio',
    label: 'Sponsor passport bio page',
    category: 'sponsor',
    required: true,
    owner: 'sponsor',
    hint: 'Sometimes the sponsor does not have a passport. If so, ADI staff will upload an alternative identity document for them.',
  },
  {
    key: 'sponsor_birth_certificate',
    label: 'Sponsor birth certificate',
    category: 'sponsor',
    required: false,
    owner: 'sponsor',
  },
  {
    key: 'sponsor_national_id',
    label: 'Sponsor national ID card',
    category: 'sponsor',
    required: false,
    owner: 'sponsor',
    hint: 'A sponsor who is a PR or dual citizen can still hold a national ID card.',
  },
  {
    key: 'sponsor_australian_id',
    label: 'Sponsor Australian ID (citizenship cert / passport / driver licence)',
    category: 'sponsor',
    required: false,
    owner: 'sponsor',
    hint: 'Any one of: Australian citizenship certificate, Australian passport, or Australian driver licence.',
  },
  {
    key: 'sponsor_other_ids',
    label: 'Sponsor other identity documents',
    category: 'sponsor',
    required: false,
    owner: 'sponsor',
    hint: 'e.g. driver licence, learner permit, photo card, Medicare card.',
  },
];

const SPONSOR_VISA_SUBCLASSES = new Set(['309', '820', '100', '801', '300']);

/**
 * Returns the sponsor-side checklist for a given visa subclass, conditioned on
 * intake answers (e.g. sponsor AU/overseas police checks, prior-relationship
 * docs). The applicant-facing label/owner is preserved.
 *
 * Sponsor-mode Documents view consumes this. Applicant-mode also consumes it
 * (alongside the applicant checklist) to render greyed "Sponsor uploads this"
 * rows so the applicant can see the full picture.
 *
 * Returns an empty list for visas that don't have a sponsor obligation in this
 * sponsor-portal flow (e.g. 461 NZ family, 600-tourist).
 */
export function getSponsorDocumentChecklist(
  visaSubclass: string,
  answers: IntakeAnswers = {},
): DocItem[] {
  // 600 family-sponsored has its own sponsor document set (invitation letter,
  // accommodation, bank statement, etc.), not the partner sponsor battery.
  // Return the sponsor-owned rows from its checklist.
  if (visaSubclass === '600-family') {
    return DOCS_600_FAMILY.filter((d) => d.owner === 'sponsor').map((d) => ({ ...d }));
  }
  if (!SPONSOR_VISA_SUBCLASSES.has(visaSubclass)) return [];

  const docs: DocItem[] = SPONSOR_OWNED_DOCS.map((d) => ({ ...d }));

  // Character declaration (Jane's matrix):
  //   Sponsor Australian police check  -> required
  //   Sponsor overseas police check    -> optional
  docs.push({
    key: 'sponsor_police_check_au',
    label: 'Sponsor: Australian police check (NPC)',
    category: 'character',
    required: true,
    owner: 'sponsor',
  });
  docs.push({
    key: 'sponsor_police_check_overseas',
    label: 'Sponsor: overseas police checks',
    category: 'character',
    required: false,
    owner: 'sponsor',
    hint: 'One per country lived in 12+ months in the last 10 years.',
  });

  // Sponsor prior-relationship docs, keyed to the sponsor's prior-relationship
  // type (Jane's "Previous relationship" rows apply to BOTH sides).
  docs.push(...priorRelationshipDocs('sponsor', answers));

  // Form 956, staff-upload-only, after lodgement. Applies to the sponsor side
  // too (the sponsor's appointment of ADI). Visible/uploadable to staff only.
  docs.push({
    key: 'sponsor_form_956',
    label: 'Form 956: Appointment of registered migration agent (sponsor)',
    category: 'forms',
    required: true,
    owner: 'staff',
    staffOnly: true,
    hint: 'Prepared by ADI and sent for signature after lodgement. Staff upload this.',
  });

  return docs;
}

/** True when the matter's visa subclass has a sponsor-mode upload flow. */
export function hasSponsorUploadFlow(visaSubclass: string): boolean {
  return visaSubclass === '600-family' || SPONSOR_VISA_SUBCLASSES.has(visaSubclass);
}

// ---------------------------------------------------------------------------
// Conditional document builders (Jane's matrix)
// ---------------------------------------------------------------------------

/**
 * Relationship-status-keyed official relationship documents (applicant side).
 *   Married  -> ceremonial + official marriage certificate, wedding photos.
 *   De Facto -> relationship certificate + proof of registration.
 *   Engaged  -> NOIM + celebrant letter + engagement photos.
 */
function relationshipDocs(answers: IntakeAnswers): DocItem[] {
  const status = answers.relationshipStatus;
  const out: DocItem[] = [];
  if (status === 'married') {
    out.push(
      { key: 'ceremonial_marriage_certificate', label: 'Ceremonial marriage certificate', category: 'relationship', required: true, hint: 'For lodgement.' },
      { key: 'official_marriage_certificate', label: 'Official marriage certificate (from registry)', category: 'relationship', required: true, hint: 'Government-issued. Can be provided after lodgement if not yet available.' },
    );
  } else if (status === 'defacto') {
    // WA and NT do not offer relationship registration, so the state-registered
    // certificate cannot exist there (R7). Skip those two documents for them.
    const registrationUnavailable = answers.registeredState === 'WA' || answers.registeredState === 'NT';
    if (!registrationUnavailable) {
      out.push(
        { key: 'relationship_certificate', label: 'State-registered relationship certificate', category: 'relationship', required: true, hint: 'Strongly strengthens a de-facto application. Mandatory if you have not been together 12+ months at lodgement.' },
        { key: 'proof_relationship_registration', label: 'Proof of relationship registration', category: 'relationship', required: true },
      );
    }
  } else if (status === 'engaged') {
    out.push(
      { key: 'noim', label: 'Notice of Intended Marriage (NOIM)', category: 'relationship', required: true, hint: 'Lodged with an Australian celebrant.' },
      { key: 'celebrant_letter', label: 'Letter from the marriage celebrant', category: 'relationship', required: true },
    );
  }
  return out;
}

/**
 * Relationship-history statement, staff/system can fill this from the
 * "Relationship history" intake tab. Applicant-owned (they confirm/upload).
 */
const RELATIONSHIP_HISTORY_STATEMENT: DocItem = {
  key: 'relationship_history_statement',
  label: 'Relationship history statement',
  category: 'relationship',
  required: true,
  hint: 'We can draft this from your "Relationship history" answers for you to confirm.',
};

/**
 * Previous-relationship documents (applies to applicant OR sponsor).
 *   Married/Divorced prior relationship -> divorce document.
 *   De facto / separated / never-married prior relationship -> statutory
 *     declaration about the previous relationship.
 *   Deceased prior partner -> death certificate (optional).
 * Only emitted when the side reported a prior relationship.
 */
function priorRelationshipDocs(side: 'applicant' | 'sponsor', answers: IntakeAnswers): DocItem[] {
  const had = side === 'applicant' ? answers.applicantHadPriorRelationship : answers.sponsorHadPriorRelationship;
  if (!had) return [];
  const type = side === 'applicant' ? answers.applicantPriorRelationshipType : answers.sponsorPriorRelationshipType;
  const deceased = side === 'applicant' ? answers.applicantPriorPartnerDeceased : answers.sponsorPriorPartnerDeceased;
  const owner: DocOwner = side;
  const prefix = side === 'applicant' ? '' : 'sponsor_';
  const sideLabel = side === 'applicant' ? '' : ' (sponsor)';
  const out: DocItem[] = [];
  if (type === 'married') {
    out.push({ key: `${prefix}divorce_document`, label: `Divorce document${sideLabel}`, category: 'relationship', required: true, owner });
  } else if (type === 'defacto' || type === 'separated' || type === 'never-married') {
    out.push({ key: `${prefix}prior_relationship_statdec`, label: `Statutory declaration regarding your previous relationship${sideLabel}`, category: 'relationship', required: true, owner });
  }
  if (deceased) {
    out.push({ key: `${prefix}prior_partner_death_certificate`, label: `Death certificate of previous partner${sideLabel}`, category: 'relationship', required: false, owner });
  }
  return out;
}

/**
 * Applicant character documents (police checks), conditioned on visa subclass
 * and whether the applicant has ever entered Australia.
 *   Australian police check: required for 820; optional for 309/300; not needed
 *     at all when the applicant has never entered Australia (309/300).
 *   Overseas police check: always required for the applicant.
 */
function applicantPoliceChecks(visaSubclass: string, answers: IntakeAnswers): DocItem[] {
  const out: DocItem[] = [];
  const neverEntered = answers.applicantEverEnteredAustralia === false;
  const isOnshore = visaSubclass === '820' || visaSubclass === '801';
  if (!neverEntered) {
    out.push({
      key: 'applicant_au_police_check',
      label: 'Applicant: Australian police check',
      category: 'character',
      required: isOnshore,
      hint: isOnshore
        ? 'Required. Anyone who has lived in Australia 12+ months in the last 10 years needs this.'
        : 'Optional for offshore applicants. Provide if you have lived in Australia 12+ months in the last 10 years.',
    });
  }
  out.push({
    key: 'applicant_overseas_police_check',
    label: 'Applicant: overseas police checks',
    category: 'character',
    required: true,
    hint: 'One per country lived in 12+ months in the last 10 years.',
  });
  return out;
}

/**
 * Form 80 + Form 888 (applicant forms).
 *   Form 80  -> required, STAFF-UPLOAD-ONLY (prepared post-lodgement, signed
 *               via third-party signing service).
 *   Form 888 -> required, applicant-owned (completed by AU citizen/PR witnesses).
 */
const APPLICANT_FORMS: DocItem[] = [
  {
    key: 'form_80',
    label: 'Form 80: Personal particulars',
    category: 'forms',
    required: true,
    owner: 'staff',
    staffOnly: true,
    hint: 'Prepared by ADI and sent for signature after lodgement. Staff upload this.',
  },
  {
    key: 'form_956',
    label: 'Form 956: Appointment of registered migration agent',
    category: 'forms',
    required: true,
    owner: 'staff',
    staffOnly: true,
    hint: 'Prepared by ADI and sent for signature after lodgement. Staff upload this.',
  },
  {
    key: 'form_888s',
    label: 'Form 888 statutory declarations from witnesses',
    category: 'forms',
    required: true,
    hint: 'Two Australian citizens or PRs over 18 who can attest to your relationship. The more, the better.',
  },
];

/**
 * Financial-aspect documents. The joint-finance Yes/No triggers come from the
 * "Financial" relationship-evidence tab (Jane's reorg 2026-07-16). Until the
 * client answers that tab the signals are undefined and the full set shows as
 * OPTIONAL evidence (Eli: show the full list); a Yes answer flips the matching
 * document to REQUIRED.
 */
function financialDocs(answers: IntakeAnswers): DocItem[] {
  return [
    { key: 'joint_bank_statements', label: 'Joint bank statement', category: 'financial', required: !!answers.jointBankAccount, hint: 'Last 6-12 months. Upload if you have one.' },
    { key: 'joint_assets_proof', label: 'Proof of joint ownership of real estate or other assets (e.g. cars, appliances)', category: 'financial', required: !!answers.jointAssets },
    { key: 'remittance_receipts', label: 'Proof of money transferred between each other regularly', category: 'financial', required: !!answers.regularMoneyTransfers },
  ];
}

/**
 * Household-aspect documents, 820 (onshore) only. Conditioned on intake.
 * Per Jane: 309/300 are offshore and the couple may not live together, so the
 * household tab/docs don't apply.
 */
function householdDocs(visaSubclass: string, answers: IntakeAnswers): DocItem[] {
  if (visaSubclass !== '820' && visaSubclass !== '801') return [];
  const out: DocItem[] = [];
  if (answers.dependentChildWithSponsor) {
    out.push({ key: 'child_responsibility_proof', label: 'Proof of joint responsibility for children', category: 'household', required: true });
  }
  if (answers.jointRentalOrOwnership) {
    out.push(
      { key: 'joint_lease_or_ownership', label: 'Joint ownership or joint rental agreement for the address you both live in', category: 'household', required: true },
      { key: 'joint_utility_bills', label: 'Joint utility bills/invoices (electricity, gas, telephone, water)', category: 'household', required: true },
    );
  } else {
    out.push(
      { key: 'living_situation_statement', label: 'Statement regarding your current living situation', category: 'household', required: true },
      { key: 'landlord_letter', label: 'Letter from your landlord (if you have no rental agreement)', category: 'household', required: false },
    );
  }
  out.push(
    { key: 'joint_mail', label: 'Mail/packages addressed to you jointly or separately at the same address', category: 'household', required: true },
    { key: 'household_photos', label: 'Pictures together at your house, your living arrangements, shared household responsibilities', category: 'household', required: true },
  );
  return out;
}

/**
 * Social-aspect documents (applicant). Conditioned on the "Social" evidence
 * tab: a Yes answer flips the matching doc to required; untouched answers
 * leave it optional.
 */
function socialDocs(answers: IntakeAnswers): DocItem[] {
  return [
    { key: 'joint_social_invitations', label: 'Joint invitations to social events (party, ceremony, etc.)', category: 'social', required: !!answers.jointInvitations },
    { key: 'photos_with_others', label: 'Pictures together with friends, family, other people', category: 'social', required: true },
    { key: 'joint_travel_evidence', label: 'Joint travel evidence (itinerary, hotel/flight bookings, photos travelling together)', category: 'social', required: !!answers.jointTravel },
    { key: 'social_media_evidence', label: 'Social media evidence of your relationship (screenshots of posts, profiles, messages)', category: 'social', required: !!answers.socialMediaVisible },
  ];
}

/**
 * Commitment-aspect documents, conditioned on relationship status and whether
 * the couple has lived separately (Jane's matrix).
 */
function commitmentDocs(answers: IntakeAnswers): DocItem[] {
  const out: DocItem[] = [];
  out.push({ key: 'wills_super_insurance', label: 'Legal wills, superannuation/insurance naming each other as beneficiary', category: 'financial', required: !!answers.willsOrBeneficiaries });
  if (answers.livedSeparately) {
    out.push({ key: 'proof_of_communication', label: 'Evidence of ongoing contact during periods of separation (correspondence, emails, phone bills)', category: 'evidence', required: true });
  }
  out.push(
    { key: 'commitment_symbols', label: 'Symbols of commitment to each other (ceremonies, rings, gifts declaring commitment)', category: 'evidence', required: false },
    { key: 'future_plans', label: 'Plans for the future', category: 'evidence', required: false },
  );
  const status = answers.relationshipStatus;
  if (status === 'married' || status === 'engaged') {
    out.push({ key: 'wedding_photos', label: 'Wedding photos', category: 'evidence', required: true });
  }
  if (status === 'engaged') {
    out.push({ key: 'engagement_photos', label: 'Engagement photos', category: 'evidence', required: true });
  }
  return out;
}

/** Catch-all "Other" row, always shown, optional. */
const OTHER_DOC: DocItem = {
  key: 'other',
  label: 'Other',
  category: 'evidence',
  required: false,
  hint: 'Any document that does not belong to a category above.',
};

// ---------------------------------------------------------------------------
// Universal conditional documents (Audit C #3), non-intake triggers
// ---------------------------------------------------------------------------
const UNIVERSAL_CONDITIONAL_DOCS: DocItem[] = [
  {
    key: 'polio_vaccination',
    label: 'Polio vaccination certificate',
    category: 'health',
    required: false,
    hint: 'Required if you spent 28+ days in a polio-risk country (Afghanistan, Cameroon, Equatorial Guinea, Ethiopia, Iraq, Israel, Nigeria, Pakistan, Somalia, or Syria) since 5 May 2014.',
  },
  {
    key: 'military_service_records',
    label: 'Military service records or discharge papers',
    category: 'character',
    required: false,
    hint: 'Required if you (or anyone in your family unit) served in the armed forces of any country in the last 10 years. Conscription counts.',
  },
  {
    key: 'name_change_evidence',
    label: 'Evidence of name change',
    category: 'identity',
    required: false,
    hint: 'Required if your current legal name is different from the name on your birth certificate or passport. Marriage certificate, deed poll, or BDM record.',
  },
  {
    key: 'form_47a',
    label: 'Form 47A: Details of child or other dependent family member aged 18+',
    category: 'forms',
    required: false,
    hint: 'Required if you have any dependents aged 18 or over included in this application.',
  },
];

/**
 * Build the full partner-class (820/801/309/100/300) checklist from intake
 * answers. This is the conditional engine's core for partner visas.
 *
 * SINGLE-STAGE (Tzvi + Eli ruling 2026-07-15). The initial partner application
 * (the 47SP + 40SP) is lodged COMPLETE: relationship evidence, supporting
 * witnesses (Form 888), and the financial/household/social pillars are all part
 * of that one lodgement, not gathered afterwards. The old design forced those
 * categories to a "stage 2 / post-lodgement" phase (former docStage rule +
 * plan §1.6), which wrongly hid them while the client prepared the application.
 * The genuine post-grant stage is a SEPARATE thing: the 47SP_stage2 form and
 * the DOCS_STAGE_2 ("fresh/updated since Stage 1") checklist used for 801/100.
 * So the initial partner checklist carries no stage tag: every item is
 * pre-lodgement and active from the start.
 */
function buildPartnerChecklist(visaSubclass: string, answers: IntakeAnswers): DocItem[] {
  return [
    ...UNIVERSAL_APPLICANT_DOCS,
    ...applicantPoliceChecks(visaSubclass, answers),
    ...APPLICANT_FORMS,
    ...getSponsorDocumentChecklist(visaSubclass, answers),
    ...relationshipDocs(answers),
    RELATIONSHIP_HISTORY_STATEMENT,
    ...priorRelationshipDocs('applicant', answers),
    ...financialDocs(answers),
    ...householdDocs(visaSubclass, answers),
    ...socialDocs(answers),
    ...commitmentDocs(answers),
    ...UNIVERSAL_CONDITIONAL_DOCS,
    OTHER_DOC,
  ];
}

// ---------------------------------------------------------------------------
// Non-partner visas (461 NZ family, 600 visitor), not driven by Jane's
// partner-visa matrix. Kept as static lists with intake-independent rows.
// ---------------------------------------------------------------------------

// 461 NZ Family
const DOCS_461: DocItem[] = [
  { key: 'applicant_passport', label: 'Applicant passport (bio page)', category: 'identity', required: true },
  { key: 'applicant_birth_certificate', label: 'Birth certificate', category: 'identity', required: true },
  { key: 'applicant_other_ids', label: 'Other photo IDs', category: 'identity', required: false },
  { key: 'nz_sponsor_passport', label: 'NZ citizen sponsor passport (bio page)', category: 'sponsor', required: true },
  { key: 'nz_sponsor_scv_evidence', label: 'NZ citizen sponsor: SCV 444 evidence (passport stamp or visa grant notice)', category: 'sponsor', required: true },
  { key: 'sponsor_form_40sp', label: 'Sponsor application form', category: 'sponsor', required: true },
  { key: 'relationship_certificate', label: 'Marriage certificate or relationship registration', category: 'relationship', required: true },
  { key: 'wedding_or_event_photos', label: 'Photos with NZ family member', category: 'evidence', required: true },
  { key: 'proof_of_financial_sharing', label: 'Proof of financial sharing or remittances', category: 'financial', required: true },
  { key: 'proof_of_communication', label: 'Proof of ongoing communication', category: 'evidence', required: true },
  { key: 'super_beneficiary', label: 'Superannuation showing the family member as beneficiary', category: 'financial', required: false },
  { key: 'joint_health_insurance', label: 'Joint health insurance', category: 'financial', required: false },
  { key: 'form_80', label: 'Form 80: Personal Particulars', category: 'forms', required: true, owner: 'staff', staffOnly: true },
  { key: 'form_956', label: 'Form 956: Appointment of registered migration agent', category: 'forms', required: true, owner: 'staff', staffOnly: true },
  { key: 'applicant_au_police_check', label: 'Applicant: Australian police check', category: 'character', required: true },
  { key: 'applicant_overseas_police_check', label: 'Applicant: overseas police checks', category: 'character', required: true },
  { key: 'sponsor_police_check_au', label: 'Sponsor: Australian police check', category: 'character', required: true, owner: 'sponsor' },
  { key: 'sponsor_police_check_overseas', label: 'Sponsor: overseas police checks', category: 'character', required: false, owner: 'sponsor' },
  { key: 'health_check_referral', label: 'Health check referral letter', category: 'health', required: true },
  ...UNIVERSAL_CONDITIONAL_DOCS,
];

// 600 Tourist (non-family-sponsored)
const DOCS_600_TOURIST: DocItem[] = [
  { key: 'applicant_passport', label: 'Applicant passport (bio page)', category: 'identity', required: true },
  { key: 'applicant_birth_certificate', label: 'Birth certificate', category: 'identity', required: false },
  { key: 'applicant_marriage_certificate', label: 'Marriage certificate (if married)', category: 'identity', required: false },
  { key: 'employer_letter', label: 'Letter from employer (confirming employment + leave dates)', category: 'evidence', required: true },
  { key: 'recent_payslips', label: 'Recent payslips (last 3 months)', category: 'financial', required: true },
  { key: 'bank_statement_cashflow', label: 'Bank statement showing cashflow', category: 'financial', required: true },
  { key: 'land_title', label: 'Proof of economic ties: land title', category: 'financial', required: false },
  { key: 'property_ownership', label: 'Proof of economic ties: property ownership', category: 'financial', required: false },
  { key: 'business_registration', label: 'Proof of business registration (if self-employed)', category: 'financial', required: false },
  { key: 'travel_itinerary', label: 'Planned travel itinerary', category: 'travel', required: true, hint: 'Flights, accommodation bookings, places you intend to visit.' },
  { key: 'relationship_to_sponsor', label: 'Proof of relationship to sponsor (if visiting family/friends)', category: 'evidence', required: false },
  {
    key: 'name_change_evidence',
    label: 'Evidence of name change',
    category: 'identity',
    required: false,
    hint: 'Required if your current legal name is different from the name on your birth certificate or passport.',
  },
];

// 600 Tourist, inviter / supporter documents.
//
// The tourist stream has no formal sponsor (that is the family-sponsored
// stream's defining feature, with its own Form 1149 and a separate sponsor
// login). But a tourist visiting family or friends usually has an Australia-
// based inviter who provides the invitation, accommodation and funds, and the
// Department wants that person evidenced. These are appended to the tourist
// checklist ONLY when the applicant has declared a supporter on Form 1419
// (application.has_supporters), so a self-funded solo tourist never sees them.
//
// owner: 'applicant' by design. There is no inviter login; the applicant
// collects these from their inviter and uploads them, the same way every
// other tourist document is handled. If a separate inviter identity/login is
// ever wanted, that is a larger, separate change.
//
// Required set confirmed by Nhu (Jane), ADI, 2026-07-15: an invited tourist's
// inviter provides ID, an invitation letter and proof of accommodation as the
// core, plus proof of funds when the inviter is funding the trip. Jane's note
// that a solo (no-inviter) applicant "needs strong financial and strong ties
// at home, which we barely see succeed" is why an inviter is the norm and this
// set is worth requiring. Financial is optional because it only applies when
// the inviter funds the visit. Status and relationship evidence are softer
// supporting items, left optional and easily promoted later.
const DOCS_600_TOURIST_INVITER: DocItem[] = [
  {
    key: 'inviter_invitation_letter',
    label: 'Invitation letter from your inviter',
    category: 'inviter',
    required: true,
    owner: 'applicant',
    hint: 'A signed letter from your inviter in Australia, stating who they are, their relationship to you, the purpose and dates of the visit, and that they invite you to stay.',
  },
  {
    key: 'inviter_id',
    label: "Inviter's ID documents",
    category: 'inviter',
    required: true,
    owner: 'applicant',
    hint: "The inviter's passport bio page, Australian citizenship certificate, or Australian photo ID. This also shows they are lawfully in Australia.",
  },
  {
    key: 'inviter_accommodation_evidence',
    label: "Inviter's proof of accommodation",
    category: 'inviter',
    required: true,
    owner: 'applicant',
    hint: "Where you will stay: the inviter's lease, rental agreement, property title, or a rates notice for the address.",
  },
  {
    key: 'inviter_financial_evidence',
    label: "Inviter's proof of finances (if they fund the trip)",
    category: 'inviter',
    required: false,
    owner: 'applicant',
    hint: 'Only if the inviter is funding the visit: recent bank statements, payslips, or a letter of employment showing they can support you.',
  },
  {
    key: 'inviter_status_evidence',
    label: "Inviter's proof of Australian status (if not shown by their ID)",
    category: 'inviter',
    required: false,
    owner: 'applicant',
    hint: 'Evidence the inviter is an Australian citizen or permanent resident, or holds a substantive visa: citizenship certificate or visa grant notice.',
  },
  {
    key: 'inviter_relationship_evidence',
    label: 'Evidence of your relationship to the inviter',
    category: 'inviter',
    required: false,
    owner: 'applicant',
    hint: 'How you know the inviter: photos together, message history, or family documents linking you.',
  },
];

// 600 Family Sponsored
const DOCS_600_FAMILY: DocItem[] = [
  ...DOCS_600_TOURIST,
  { key: 'form_1149', label: 'Form 1149: Sponsorship for Visitor Visa', category: 'forms', required: true },
  { key: 'form_1229_minor', label: 'Form 1229: Consent to grant of visa to a child (if applicant is a minor)', category: 'forms', required: false },
  { key: 'sponsor_form_80', label: 'Sponsor: Form 80 (Personal Particulars)', category: 'forms', required: true, owner: 'staff', staffOnly: true, hint: 'Home Affairs requires Form 80 from the Australian sponsor on the family-sponsored stream.' },
  { key: 'sponsor_passport_bio', label: 'Australian sponsor passport / citizenship', category: 'sponsor', required: true, owner: 'sponsor' },
  { key: 'sponsor_invitation_letter', label: 'Sponsor invitation letter', category: 'sponsor', required: true, owner: 'sponsor' },
  { key: 'sponsor_lease_or_accommodation', label: 'Sponsor: proof of lease or accommodation', category: 'sponsor', required: true, owner: 'sponsor' },
  { key: 'sponsor_bank_statement', label: 'Sponsor: bank statement (AUD 5,000-10,000)', category: 'sponsor', required: true, owner: 'sponsor' },
  { key: 'sponsor_employment', label: 'Sponsor: proof of employment', category: 'sponsor', required: true, owner: 'sponsor' },
  { key: 'sponsor_payslips', label: 'Sponsor: payslips', category: 'sponsor', required: true, owner: 'sponsor' },
  {
    key: 'form_47a',
    label: 'Form 47A: Details of child or other dependent family member aged 18+',
    category: 'forms',
    required: false,
    hint: 'Required if you have any dependents aged 18 or over included in this application.',
  },
];

// 801 / 100 Stage 2 evidence
const DOCS_STAGE_2: DocItem[] = [
  { key: 'doha_invitation', label: 'Department of Home Affairs Stage 2 invitation', category: 'forms', required: true },
  { key: 'fresh_au_police_check', label: 'Fresh Australian police check', category: 'character', required: true, hint: 'Less than 12 months old at lodgement.' },
  { key: 'fresh_form_888s', label: 'Two fresh Form 888 statutory declarations', category: 'forms', required: true, hint: 'Same witnesses as Stage 1 are fine if they remain in your life.' },
  { key: 'updated_joint_bank_statements', label: 'Updated joint bank statements (since Stage 1)', category: 'financial', required: true },
  { key: 'updated_lease_or_property', label: 'Current lease, mortgage, or property documents', category: 'financial', required: true },
  { key: 'updated_health_insurance', label: 'Current joint health insurance policy', category: 'financial', required: false },
  { key: 'updated_super_beneficiary', label: 'Current super beneficiary nomination', category: 'financial', required: false },
  { key: 'children_birth_certificates', label: 'Birth certificates of any children of the relationship', category: 'evidence', required: false },
  { key: 'updated_joint_photos', label: 'Updated joint photos since Stage 1', category: 'evidence', required: true },
  {
    key: 'polio_vaccination',
    label: 'Polio vaccination certificate',
    category: 'health',
    required: false,
    hint: 'Required if you spent 28+ days in a polio-risk country since your Stage 1 grant. See Stage 1 list for the country list.',
  },
  {
    key: 'military_service_records',
    label: 'Military service records or discharge papers',
    category: 'character',
    required: false,
    hint: 'Required if you (or anyone in your family unit) served in any country’s armed forces since Stage 1 grant.',
  },
  {
    key: 'name_change_evidence',
    label: 'Evidence of name change',
    category: 'identity',
    required: false,
    hint: 'Required if your current legal name has changed since Stage 1.',
  },
];

interface ChecklistOptions {
  /** When true, append Form 1229 (consent for child under 18) to the list. */
  hasMinorDependents?: boolean;
  /** Normalised intake answers driving the conditional engine. */
  answers?: IntakeAnswers;
  /**
   * 600 tourist only: an Inviter party is linked on the matter
   * (matters.sponsor_contact_id is set). A second, independent trigger for the
   * inviter document set alongside `answers.hasInviter`, which comes from the
   * applicant's own funding answer (application.has_supporters). Staff pick the
   * inviter at matter creation, so the party can exist long before the
   * applicant reaches the funding question, and the inviter documents must be
   * visible from that moment. Either signal is sufficient; neither is required
   * of the other.
   */
  hasInviterParty?: boolean;
}

/**
 * Public API: get the document checklist for a given visa subclass + variant,
 * conditioned on the matter's intake answers and matter-specific situations
 * (e.g. minor dependents).
 *
 * INPUTS  -> visaSubclass, variant, opts.answers (IntakeAnswers), opts flags.
 * OUTPUT  -> DocItem[] where each item is required/optional and carries an
 *            owner (applicant/sponsor/staff). Hidden documents are omitted
 *            entirely (the conditional builders only emit a row when its
 *            trigger fires).
 *
 * Backwards-compatible: callers that pass no `answers` get the safe-default
 * checklist (conditional intake-driven rows stay hidden until answers arrive).
 */
export function checklistForVisa(
  visaSubclass: string,
  variant: string | null,
  opts?: ChecklistOptions,
): DocItem[] {
  const answers = normaliseAnswers(opts?.answers, variant);
  let base: DocItem[];

  if (visaSubclass === '820' || visaSubclass === '309' || visaSubclass === '300') {
    base = buildPartnerChecklist(visaSubclass, answers);
  } else if (visaSubclass === '801' || visaSubclass === '100') {
    base = DOCS_STAGE_2;
  } else if (visaSubclass === '461') {
    base = DOCS_461;
  } else if (visaSubclass === '600-tourist') {
    base = DOCS_600_TOURIST;
  } else if (visaSubclass === '600-family') {
    base = DOCS_600_FAMILY;
  } else {
    // Default to onshore partner (820) so an unknown subclass still renders the
    // full partner checklist.
    base = buildPartnerChecklist('820', answers);
  }

  // B12-D1 fix: DOCS_461 carried a "Sponsor application form" item
  // (sponsor_form_40sp) left over from copy-pasting a partner-visa
  // checklist, but 461 has no separate sponsor form at all, the NZ citizen
  // family member's details are captured inline on Form 147
  // (hasSponsorForm is the single source of truth SponsorPanel.tsx,
  // pickFormId and IntakeFormV2 already gate on; see
  // src/lib/schema-runtime/formRouting.ts). Filtered here generically
  // (not just for 461) so no current-or-future no-sponsor-form visa can
  // carry a checklist item promising a form that structurally can't exist.
  if (!hasSponsorForm(visaSubclass)) {
    base = base.filter((d) => d.key !== 'sponsor_form_40sp');
  }

  // Conditional augmentation: 600 tourist with a declared inviter/supporter ->
  // the inviter document set. Appended (not baked into DOCS_600_TOURIST) so a
  // self-funded solo tourist who answered "no supporter" never sees them.
  // TWO signals, either sufficient (Wave 5): the applicant's own funding answer
  // (answers.hasInviter, from application.has_supporters) OR an Inviter party
  // linked on the matter by staff (opts.hasInviterParty). A tourist matter with
  // NEITHER is untouched and shows no inviter rows, exactly as before.
  if (
    visaSubclass === '600-tourist' &&
    (answers.hasInviter || opts?.hasInviterParty) &&
    !base.some((d) => d.category === 'inviter')
  ) {
    base = [...base, ...DOCS_600_TOURIST_INVITER];
  }

  // Conditional: a self-employed 600 tourist applicant (item 28) must provide
  // proof of business registration, so flip that row from optional to required.
  if (visaSubclass === '600-tourist' && answers.applicantSelfEmployed) {
    base = base.map((d) => (d.key === 'business_registration' ? { ...d, required: true } : d));
  }

  // Conditional augmentation: minor migrating dependents -> Form 1229.
  if (opts?.hasMinorDependents && !base.some((d) => d.key === 'form_1229_minor')) {
    return [
      ...base,
      {
        key: 'form_1229_minor',
        label: 'Form 1229: Consent to grant of visa to a child (one per minor dependent)',
        category: 'forms',
        required: true,
        hint: 'Required for any dependent under 18 included in this application. Both parents must sign.',
      },
    ];
  }

  return base;
}

/**
 * Fold the matter `variant` (legacy married/defacto signal set at matter
 * creation) into the intake answers as a fallback for relationshipStatus, so
 * matters created before the intake form is filled still get the right
 * relationship docs.
 */
function normaliseAnswers(answers: IntakeAnswers | undefined, variant: string | null): IntakeAnswers {
  const out: IntakeAnswers = { ...(answers ?? {}) };
  if (!out.relationshipStatus && variant) {
    if (variant === 'married') out.relationshipStatus = 'married';
    else if (variant === 'defacto' || variant === 'de-facto' || variant === 'de_facto') out.relationshipStatus = 'defacto';
    else if (variant === 'engaged') out.relationshipStatus = 'engaged';
  }
  return out;
}

export const CATEGORY_LABELS: Record<DocCategory, string> = {
  identity: 'Identity',
  relationship: 'Relationship',
  character: 'Character',
  health: 'Health',
  evidence: 'Evidence',
  sponsor: 'Sponsor',
  // Displayed as "Inviter" since Wave 5 (Tzvi ruling 2026-07-24: the 600
  // tourist stream says Inviter everywhere client- and staff-facing). This
  // supersedes the 2026-07-15 "the inviter will be the sponsor, same thing for
  // now" call; keeping it a distinct category is exactly what made the split
  // a label change rather than a document rework. The category only ever
  // appears on a 600-tourist checklist, so no other subclass is affected.
  inviter: 'Inviter',
  forms: 'Forms',
  travel: 'Travel',
  financial: 'Financial',
  household: 'Household',
  social: 'Social',
};
